import { useState, useCallback, useMemo } from 'react';
import { AppHeader } from './components/AppHeader';
import manifestData from './data/manifest.json';
import { AppFooter } from './components/AppFooter';
import { SearchBar } from './components/SearchBar';
import { ActColumn, openRecitalGroupReader, openAnnexReader } from './components/ActColumn';
import { Timeline } from './components/Timeline';
import { ArticleReaderPage } from './components/ArticleReaderPage';
import { MandateViewerPage } from './components/MandateViewerPage';
import { MandateMatrix } from './components/MandateMatrix';
import { GlossaryReaderPage } from './components/GlossaryReaderPage';
import { StatsViewerPage } from './components/StatsViewerPage';
import { useHashRoute, parseArticleRoute, parseGlossaryRoute, parseMandateRoute, articleHashFor, mandateHashFor, glossaryHashFor } from './hooks/useHashRoute';
import { useSemanticSearch, type SemanticResult } from './semantic/useSemanticSearch';
import { useFilterState } from './hooks/useFilterState';
import { useTimelineFilterState } from './hooks/useTimelineFilterState';
import type { Instrument, ActId, Act, RecitalGroup, Annex } from './types';
import instrumentsData from './data/instruments/index.json';
import styles from './App.module.css';

// Act data imports
import amldData from './data/acts/amld.json';
import amlrData from './data/acts/amlr.json';
import amlarData from './data/acts/amlar.json';
import tfrData from './data/acts/tfr.json';

const ACTS = [amldData, amlrData, amlarData, tfrData] as const;
const ALL_ACT_IDS: ActId[] = ['amld', 'amlr', 'amlar', 'tfr'];
const INSTRUMENTS = instrumentsData as Record<string, Instrument>;
const SEMANTIC_KEY = 'aml_semantic_search';

export default function App() {
  // Route gate — if URL is #/article/<actId>/<num>, render the standalone article reader.
  //                if URL is #/glossary or #/glossary/<id>, render the standalone glossary reader.
  const hash = useHashRoute();

  // Hidden analytics dashboard — reachable at /user-stats (path) or #/stats (hash).
  // Not linked anywhere in the tool; the footer popup is unchanged.
  if (typeof window !== 'undefined' && /^\/user-stats\/?$/.test(window.location.pathname)) return <StatsViewerPage />;

  const articleRoute = parseArticleRoute(hash);
  if (articleRoute) return <ArticleReaderPage actId={articleRoute.actId} num={articleRoute.num} />;
  const mandateRoute = parseMandateRoute(hash);
  if (mandateRoute) {
    const inst = INSTRUMENTS[mandateRoute.id] as any;
    return mandateRoute.art
      ? <ArticleReaderPage actId={inst?.actId || 'amlr'} num={mandateRoute.art} mandateId={mandateRoute.id} />
      : <MandateViewerPage id={mandateRoute.id} />;
  }
  const glossaryRoute = parseGlossaryRoute(hash);
  if (glossaryRoute !== null) return <GlossaryReaderPage initialEntryId={glossaryRoute || undefined} />;

  // Hidden analytics dashboard — reachable only by typing #/stats. Not linked anywhere in the tool.
  if (/^#\/stats\/?$/i.test(hash)) return <StatsViewerPage />;

  return <MainView />;
}

// The main four-column view lives in its own component so that the route gate above can
// return early without changing the number of hooks rendered by <App/> between hash
// changes (React error #300 "rendered fewer hooks than expected" when navigating
// home → #/mandate/… in the same tab).
function MainView() {
  const { filters, isActive, setQuery, toggleHasMandates, toggleType, toggleStatus, clearFilters } =
    useFilterState();
  const {
    tlFilters, isTimelineFilterActive,
    setPeriod, toggleTimelineType, toggleVerification, clearTimelineFilters,
  } = useTimelineFilterState();

  const [view, setView] = useState<'columns' | 'mandates' | 'timeline'>('columns');
  const [collapseSignal, setCollapseSignal] = useState(0);
  // Articles passing the current filters, per visible act (search-bar counter)
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  const onVisibleCount = useCallback((actId: string, n: number) => setVisibleCounts(prev => (prev[actId] === n ? prev : { ...prev, [actId]: n })), []);
  const [crossRefTarget, setCrossRefTarget] = useState<{actId: string; num: number | string; ts: number} | null>(null);
  // Laptops (<1400px): start with three columns so titles and badges stay readable; TFR is one Focus-on click away.
  const [visibleActs, setVisibleActs] = useState<Set<ActId>>(() => new Set(typeof window !== 'undefined' && window.innerWidth < 1400 && window.innerWidth > 900 ? ALL_ACT_IDS.filter(a => a !== 'tfr') : ALL_ACT_IDS));

  // Search by meaning — in-browser semantic search (worker + static index). Off by default;
  // the choice is remembered. While on, the query box drives the semantic panel and does
  // NOT filter the columns (keyword filtering is a different mental model).
  const [semanticOn, setSemanticOn] = useState<boolean>(() => { try { return localStorage.getItem(SEMANTIC_KEY) === '1'; } catch { return false; } });
  const toggleSemantic = useCallback(() => setSemanticOn(v => { const n = !v; try { localStorage.setItem(SEMANTIC_KEY, n ? '1' : '0'); } catch { /* ignore */ } return n; }), []);
  const semantic = useSemanticSearch(semanticOn, filters.query);
  const columnFilters = useMemo(() => (semanticOn ? { ...filters, query: '' } : filters), [semanticOn, filters]);
  const columnFiltersActive = semanticOn ? (filters.hasMandates || filters.types.length > 0 || filters.statuses.length > 0) : isActive;
  const mandateLabels = useMemo(() => Object.fromEntries(Object.entries(INSTRUMENTS).map(([id, m]) => [id, { type: m.type, actId: m.actId, legalBasis: m.legalBasis, title: m.title }])), []);

  const toggleAct = useCallback((actId: ActId) => {
    setVisibleActs(prev => {
      // Never hide the last visible act
      if (prev.has(actId) && prev.size === 1) return prev;
      const next = new Set(prev);
      if (next.has(actId)) next.delete(actId); else next.add(actId);
      return next;
    });
  }, []);

  const handleCrossRefClick = (actId: string, num: number | string) => {
    // If target act is hidden, open the standalone article reader in a new tab
    if (!visibleActs.has(actId as ActId)) {
      window.open(articleHashFor(actId, num), '_blank', 'noopener');
      return;
    }

    setCrossRefTarget({ actId, num, ts: Date.now() });
    setTimeout(() => {
      const el = document.getElementById(`${actId}-${num}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        requestAnimationFrame(() => {
          el.classList.remove('xref-highlight');
          void el.offsetWidth;
          el.classList.add('xref-highlight');
          setTimeout(() => el.classList.remove('xref-highlight'), 1600);
        });
      }
    }, 250);
  };

  // Semantic hit → primary action (column / reader) and secondary action (new tab)
  const openSemanticHitTab = useCallback((hit: SemanticResult) => {
    const c = hit.meta;
    if (c.m) { window.open(mandateHashFor(c.m, c.k === 'mart' ? c.n : undefined), '_blank', 'noopener'); return; }
    if (c.k === 'def') { window.open(glossaryHashFor(String(c.n)), '_blank', 'noopener'); return; }
    if (c.k === 'art' && c.a) window.open(articleHashFor(c.a, c.n), '_blank', 'noopener');
  }, []);
  const openSemanticHit = useCallback((hit: SemanticResult) => {
    const c = hit.meta;
    const act = ACTS.find(a => a.id === c.a) as unknown as Act | undefined;
    if (c.k === 'art' && c.a) { handleCrossRefClick(c.a, c.n); return; }
    if (c.k === 'rec' && act) {
      const groups = (act.recitalGroups || []) as RecitalGroup[];
      const g = groups.find(g => g.recitalNums.includes(Number(c.n)));
      if (g) { openRecitalGroupReader(g, act, act.id); return; }
    }
    if (c.k === 'anx' && act) {
      const ax = (act.annexes || []).find((x: Annex) => x.id === c.n);
      if (ax) { openAnnexReader(ax, act.id, act); return; }
    }
    openSemanticHitTab(hit);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleActs, openSemanticHitTab]);

  const handleInstrumentClick = useCallback((instrumentId: string) => {
    if (INSTRUMENTS[instrumentId]) window.open(mandateHashFor(instrumentId), '_blank', 'noopener');
  }, []);

  // Return to the main page: switch to the default columns view and scroll to top.
  // Filters, search, act visibility and expanded articles are intentionally left
  // untouched. Triggered by clicking the header title or the EU stars — no reload.
  const handleGoHome = useCallback(() => {
    setView('columns');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const instrumentCount = Object.keys(INSTRUMENTS).length;

  return (
    <div className={styles.app}>
      <AppHeader instrumentCount={instrumentCount} dataDate={manifestData.generated_at?.slice(0,10)} onGoHome={handleGoHome} />

      <SearchBar
        filters={filters}
        isActive={columnFiltersActive}
        onQueryChange={setQuery}
        onToggleHasMandates={toggleHasMandates}
        onToggleType={toggleType}
        onToggleStatus={toggleStatus}
        onClearFilters={clearFilters}
        onCollapseAll={() => setCollapseSignal(s => s + 1)}
        matchCount={view === 'columns' && columnFiltersActive ? ACTS.filter(a => visibleActs.has(a.id as ActId)).reduce((s, a) => s + (visibleCounts[a.id] ?? 0), 0) : undefined}
        view={view}
        onSetView={setView}
        visibleActs={visibleActs}
        onToggleAct={toggleAct}
        tlFilters={tlFilters}
        isTimelineFilterActive={isTimelineFilterActive}
        onSetPeriod={setPeriod}
        onToggleTimelineType={toggleTimelineType}
        onToggleVerification={toggleVerification}
        onClearTimelineFilters={clearTimelineFilters}
        semanticOn={semanticOn}
        onToggleSemantic={toggleSemantic}
        semantic={semantic}
        mandates={mandateLabels}
        onOpenSemanticHit={openSemanticHit}
        onOpenSemanticHitTab={openSemanticHitTab}
      />

      {view === 'columns' && (
        <div className={styles.columns}>
          {ACTS.filter(act => visibleActs.has(act.id as ActId)).map(act => (
            <ActColumn
              key={act.id}
              act={act as never}
              filters={columnFilters}
              onInstrumentClick={handleInstrumentClick}
              collapseSignal={collapseSignal}
              onCrossRefClick={handleCrossRefClick}
              crossRefTarget={crossRefTarget?.actId === act.id ? crossRefTarget : undefined}
              onVisibleCount={onVisibleCount}
            />
          ))}
        </div>
      )}

      {view === 'mandates' && <MandateMatrix visibleActs={visibleActs} filters={columnFilters} />}

      {view === 'timeline' && <Timeline tlFilters={tlFilters} visibleActs={visibleActs} />}

      <AppFooter />
    </div>
  );
}
